'use client'

import { useId } from 'react'

import { cn } from '../../lib/utils'

export default function NoiseBackground({
    className,
    variant = 'default',
}: {
    className?: string
    /** Lower contrast, softer noise, and a clearer fade toward the bottom */
    variant?: 'default' | 'subtle'
}) {
    const filterId = useId().replace(/:/g, '')
    const subtle = variant === 'subtle'

    const gradientBackground = subtle
        ? `radial-gradient(circle at 100% 0%, oklch(from var(--chart-1) calc(l * 1.05) calc(c * 1.15) h / 0.18) 0%, transparent 50%),
            radial-gradient(circle at 0% 100%, oklch(from var(--chart-2) calc(l * 1.05) calc(c * 1.1) h / 0.12) 0%, transparent 100%),
            var(--background)`
        : `radial-gradient(circle at 100% 0%, oklch(from var(--chart-1) calc(l * 1.2) calc(c * 1.5) h / 0.6) 0%, transparent 45%),
            radial-gradient(circle at 0% 100%, oklch(from var(--chart-2) calc(l * 1.2) calc(c * 1.4) h / 0.4) 0%, transparent 100%),
            var(--background)`

    return (
        <div
            aria-hidden
            className={cn(
                'absolute inset-0',
                subtle &&
                    '[mask-image:linear-gradient(to_bottom,black_0%,black_36%,transparent_92%)] [-webkit-mask-image:linear-gradient(to_bottom,black_0%,black_36%,transparent_92%)]',
                className,
            )}>
            <div
                className={cn('absolute inset-0 z-0 rotate-[44deg]', subtle && 'opacity-80')}
                style={{
                    background: gradientBackground,
                    filter: subtle ? 'blur(160px)' : 'blur(120px)',
                }}
            />
            <div
                className={cn(
                    'pointer-events-none absolute inset-0 z-10',
                    subtle
                        ? '[mask-image:linear-gradient(to_bottom,black_32%,black_45%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_bottom,black_32%,black_45%,transparent_100%)]'
                        : '[mask-image:linear-gradient(to_bottom,black_55%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_bottom,black_55%,transparent_100%)]',
                )}>
                <svg
                    className={cn('h-full w-full', subtle ? 'opacity-[0.22]' : 'opacity-100')}
                    style={{ mixBlendMode: subtle ? 'soft-light' : 'overlay' }}>
                    <filter id={filterId}>
                        <feTurbulence
                            type="fractalNoise"
                            baseFrequency={subtle ? '0.65' : '0.8'}
                            numOctaves={subtle ? '3' : '4'}
                            stitchTiles="stitch"
                            seed="10"
                        />
                        <feColorMatrix type="saturate" values="0" />
                    </filter>
                    <rect width="100%" height="100%" filter={`url(#${filterId})`} fill="white" />
                </svg>
            </div>
        </div>
    )
}
